import React from 'react'
import DetailRow from './DetailRow'
import EmptyCardMessage from './EmptyCardMessage'
import {
  editStartValue,
  type DetailRow as DetailRowModel,
} from '@/utils/consumerDetailCards'

export type LocationOption = { locationId: string; locationName: string }

export interface DetailRowsProps {
  rows: DetailRowModel[]
  data?: Record<string, any>
  isEditMode?: boolean
  editedData?: Record<string, any>
  onFieldChange?: (field: string, value: any) => void
  locations?: LocationOption[]
  emptyMessage: string
}

/** The rows of one card. A row with an edit field becomes an input in edit mode; every other row stays text. */
const DetailRows: React.FC<DetailRowsProps> = ({
  rows,
  data = {},
  isEditMode = false,
  editedData = {},
  onFieldChange,
  locations = [],
  emptyMessage,
}) => {
  if (rows.length === 0) return <EmptyCardMessage message={emptyMessage} />

  return (
    <>
      {rows.map(row => {
        const field = row.editField
        const isLocation = field === 'locationId'
        const canEdit = !!field && (!isLocation || locations.length > 0)
        const current = field
          ? String(editedData[field] ?? editStartValue(data, field))
          : row.value
        const options = isLocation
          ? withCurrent(locations, current, row.value)
          : []

        return (
          <DetailRow
            key={row.key}
            label={row.label}
            value={isEditMode && canEdit ? current : row.value}
            isEditMode={isEditMode}
            isEditable={canEdit}
            disableIfEmpty={false}
            editType={isLocation ? 'dropdown' : 'text'}
            dropdownOptions={options}
            onChange={value => field && onFieldChange?.(field, String(value))}
          />
        )
      })}
    </>
  )
}

/** The list always holds the current value, so a path the site has no name for does not blank the select. */
const withCurrent = (
  locations: LocationOption[],
  current: string,
  shownName: string
) => {
  const options = locations.map(l => ({
    value: l.locationId,
    label: l.locationName,
  }))
  return current === '' || options.some(o => o.value === current)
    ? options
    : [{ value: current, label: shownName }, ...options]
}

export default DetailRows
